import { 
  ContractType, 
  ContractTypeDisplayShort, 
  DealStates, DeploymentTypes, 
  DeploymentTypeShortDisplay, 
  PricingType, 
  RevenueSource 
} from '~/constants';
import { getProjectBidOverallAmount } from '~/utils/calculators/costsFns';
import { formatNumber } from '~/utils/formatNumber';
import { Project } from '~/types/project';


const getOfftakeRate = (project: Project, isCondensed = false) => {
  let value = isCondensed ? 'TBD': 'Rate TBD';
  const bid = project?.pendingBid || project?.acceptedBid;

  //If we bring the full portfolio summary table back, we do not have logic for CS rate with revenue assumptions  
  
  if (project.contractType === ContractType.UNCONTRACTED && project.contractRate && project.contractRate !== 0) {
    value = `$${formatNumber(project.contractRate, 4)} ${isCondensed ? '' : 'per kWh'}`;
  } else if (project.pricingType === PricingType.OFFTAKE && project.contractType === ContractType.PPA && bid) {
    // Is OFFTAKE pricing type and is PPA contract type
    value = `$${formatNumber(bid?.amount, 4)} ${isCondensed ? '' : 'per kWh'}`;
  } else if (project.pricingType === PricingType.OFFTAKE && bid) {
    // Is OFFTAKE pricing type and is LEASE or ESA contract type
    value = `$${formatNumber(getProjectBidOverallAmount(project, bid), 0)} ${isCondensed ? '' : 'per year'}`;
  } else if (project?.contractRate && project?.contractType === ContractType.PPA) {
    // Is PPA contract type with a contract rate set
    value = `$${formatNumber(project?.contractRate, 4)} ${isCondensed ? '' : 'per kWh'}`;
  } else if (project?.contractRate) {
    // Is LEASE or ESA contract type with a contract rate set
    // If project is in a portfolio, display the rate per kWh
    value = `$${formatNumber(project?.contractRate, 0)} per year`;
  } else if (!project.revenueAssumptionsFlag && project?.deploymentType === DeploymentTypes.COMMUNITY_SOLAR) {
    // Is community solar project with no revenue assumptions
    value = 'CSG';
  }
  // Return TBD if none of the above scenarios apply
  return value;
};

const getOfftakeValues = (project: Project, isCondensed = false) => {
  const type = project?.contractType ? ContractTypeDisplayShort[project.contractType] : undefined;
  // This is a quick fix for use on portfolio summary table and could be refactored
  const deploymentType = DeploymentTypeShortDisplay[project?.deploymentType as keyof typeof DeploymentTypeShortDisplay];
  
  const term = project?.contractTerm != null
  ? `${project.contractTerm} year${project.contractTerm > 1 && isCondensed ? 's' : ''}`
  : isCondensed ? 'TBD' : 'Term TBD';

  const escalator = project?.contractEscalator != null
    ? `${project.contractEscalator}%${isCondensed ? '' : ' Escalator'}`
    : isCondensed ? 'TBD' : 'Escalator TBD';

  return { type, deploymentType, term, escalator };
};

export default function offtakeRate(project: Project) {
  //rate is not currently in use for portfolio summary table
  const rate = getOfftakeRate(project, true);
  const { type, deploymentType, term, escalator } = getOfftakeValues(project, true);
  
  let offtakeRateValueString: string;
  if (project.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS && !project.revenueAssumptionsFlag) {
    offtakeRateValueString = 'Community solar — investor to make own assumptions';
  } else if (project.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS) {
    offtakeRateValueString = 'Community solar - see data for recommended assumptions';
  } else {
    const rate = getOfftakeRate(project);
    const { type, term, escalator } = getOfftakeValues(project);
    offtakeRateValueString = `${type}: ${rate}, ${term}, ${escalator}`;
  }
   
  return {
    isDisplaying: !!project.contractType,
    isHighlighted: project?.dealState >= DealStates.IN_DILIGENCE && project?.pricingType === PricingType.OFFTAKE,
    value: offtakeRateValueString,
    type,
    deploymentType,
    rate,
    term,
    escalator
  };
}